


























































































































































































































































































import {
  convertAppliedPropsToItemProps,
  createTabletRenderer,
} from '@oare/oare';
import {
  LinkRow,
  ResourceRow,
  TextDiscourseRow,
  TextDraft,
  TextPhoto,
} from '@oare/types';
import {
  defineComponent,
  reactive,
  ref,
  onMounted,
  computed,
  InjectionKey,
  provide,
  ComputedRef,
  PropType,
} from '@vue/composition-api';
import sl from '@/serviceLocator';
import {
  EpigraphyResponse,
  TranslitOption,
  EpigraphyLabelLink,
  ZoteroData,
  LocaleCode,
} from '@oare/types';
import EpigraphyEditor from './Editor/EpigraphyEditor.vue';
import { getLetterGroup } from '../CollectionsView/utils';
import Stoplight from './EpigraphyDisplay/components/Stoplight.vue';
import EpigraphyImage from './EpigraphyDisplay/components/EpigraphyImage.vue';
import EpigraphyFullDisplay from './EpigraphyDisplay/EpigraphyFullDisplay.vue';
import AddPhotos from '@/views/Texts/CollectionTexts/AddTexts/Photos/AddPhotos.vue';
import { addDetailsToTextPhotos } from '../CollectionTexts/AddTexts/utils/photos';
import { v4 } from 'uuid';
import i18n from '@/i18n';

export interface DraftContent extends Pick<TextDraft, 'content' | 'notes'> {
  uuid: string | null;
}

export interface OriginalTextInfo {
  excavationPrefix: string | null;
  excavationNumber: string | null;
  museumPrefix: string | null;
  museumNumber: string | null;
  primaryPublicationPrefix: string | null;
  primaryPublicationNumber: string | null;
}

export const EpigraphyReloadKey: InjectionKey<() => Promise<void>> = Symbol();

export default defineComponent({
  name: 'EpigraphyView',
  components: {
    EpigraphyEditor,
    Stoplight,
    EpigraphyImage,
    EpigraphyFullDisplay,
    AddPhotos,
  },
  props: {
    textUuid: {
      type: String,
      required: false,
    },
    discourseToHighlight: {
      type: String,
      required: false,
    },
    highlightEpigraphyDiscourse: {
      type: Boolean,
      default: true,
    },
    highlightDiscourse: {
      type: Boolean,
      default: false,
    },
    disableEditing: {
      type: Boolean,
      default: false,
    },
    localEpigraphyUnits: {
      type: Object as PropType<EpigraphyResponse>,
      required: false,
    },
    localImageUrls: {
      type: Array as PropType<EpigraphyLabelLink[]>,
      required: false,
    },
    localDiscourseInfo: {
      type: Array as PropType<TextDiscourseRow[]>,
      required: false,
    },
    forceAllowAdminView: {
      type: Boolean,
      default: false,
    },
  },

  setup({
    textUuid,
    discourseToHighlight,
    localEpigraphyUnits,
    localImageUrls,
    forceAllowAdminView,
    highlightDiscourse,
    highlightEpigraphyDiscourse,
    disableEditing,
  }) {
    const store = sl.get('store');
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const router = reactive(sl.get('router'));

    const hasEditPermission = computed(() =>
      store.hasPermission('EDIT_TEXT_INFO')
    );

    const canAddPictures = computed(() =>
      store.hasPermission('UPLOAD_EPIGRAPHY_IMAGES')
    );

    const canComment = computed(
      () => store.hasPermission('ADD_COMMENTS') && !disableEditing
    );

    const loading = ref(false);
    const imagesLoading = ref(false);

    const commentMode = ref(false);

    const draft = ref<DraftContent | null>(null);
    const hasPicture = computed(() => imageUrls.value.length > 0);
    const textInfo = ref<EpigraphyResponse>({
      canWrite: false,
      text: {
        uuid: '',
        type: '',
        name: '',
        excavationPrefix: '',
        excavationNumber: '',
        museumPrefix: '',
        museumNumber: '',
        publicationPrefix: '',
        publicationNumber: '',
      },
      collection: {
        uuid: '',
        name: '',
      },
      cdliNum: '',
      units: [],
      color: '',
      colorMeaning: '',
      discourseUnits: [],
      hasEpigraphy: false,
      zoteroData: [],
    });
    const imageUrls = ref<EpigraphyLabelLink[]>([]);

    let editText = ref(false);

    const originalTextInfoObject = ref<OriginalTextInfo>({
      excavationPrefix: null,
      excavationNumber: null,
      museumPrefix: null,
      museumNumber: null,
      primaryPublicationPrefix: null,
      primaryPublicationNumber: null,
    });
    const updateDraft = (newDraft: DraftContent) => (draft.value = newDraft);

    const zoteroDataList = ref<ZoteroData[]>([]);
    const seeMoreZotero = ref<boolean>(false);

    const allowViewCitations = computed(() =>
      store.hasPermission('VIEW_TEXT_CITATIONS')
    );

    const breadcrumbItems = computed(() => {
      const letterGroup = getLetterGroup(textInfo.value.collection.name);

      return [
        {
          link: '/collections/A-J',
          text: 'Texts',
        },
        {
          link: `/collections/${letterGroup}`,
          text: letterGroup,
        },
        {
          link: `/collections/name/${textInfo.value.collection.uuid}`,
          text: textInfo.value.collection.name,
        },
      ];
    });

    const isEditing = computed(
      () => router.currentRoute.name === 'epigraphyEditor'
    );

    const routeProps = computed(() => {
      if (router.currentRoute.name === 'epigraphyEditor') {
        return {
          draft: draftContent.value,
        };
      }

      return {
        epigraphicUnits: textInfo.value.units,
        discourseUnits: textInfo.value.discourseUnits,
        discourseToHighlight,
        commentMode: commentMode.value,
        textUuid,
        highlightEpigraphyDiscourse,
        highlightDiscourse,
      };
    });

    const routeActions = computed(() => {
      if (router.currentRoute.name === 'epigraphyEditor') {
        return {
          'save-draft': updateDraft,
        };
      }

      return {};
    });

    const draftContent = computed<DraftContent>(() => {
      if (draft.value) {
        return draft.value;
      }

      const draftRenderer = createTabletRenderer(
        textInfo.value.units,
        i18n.locale as LocaleCode,
        {
          lineNumbers: true,
        }
      );
      return {
        uuid: null,
        content: draftRenderer.sides.map(side => ({
          side: side.side!,
          text: draftRenderer.sideReading(side.side!),
        })),
        notes: '',
      };
    });

    const isAdmin = computed(() => store.getters.isAdmin);

    const canViewEpigraphyImages = computed(
      () => hasPicture.value && store.hasPermission('VIEW_EPIGRAPHY_IMAGES')
    );

    const toggleTextInfo = function () {
      originalTextInfoObject.value.excavationPrefix =
        textInfo.value.text.excavationPrefix;
      originalTextInfoObject.value.excavationNumber =
        textInfo.value.text.excavationNumber;
      originalTextInfoObject.value.museumPrefix =
        textInfo.value.text.museumPrefix;
      originalTextInfoObject.value.museumNumber =
        textInfo.value.text.museumNumber;
      originalTextInfoObject.value.primaryPublicationPrefix =
        textInfo.value.text.publicationPrefix;
      originalTextInfoObject.value.primaryPublicationNumber =
        textInfo.value.text.publicationNumber;

      editText.value = !editText.value;
    };

    const cancelEditTextInfo = function () {
      textInfo.value.text.excavationPrefix =
        originalTextInfoObject.value.excavationPrefix;
      textInfo.value.text.excavationNumber =
        originalTextInfoObject.value.excavationNumber;
      textInfo.value.text.museumPrefix =
        originalTextInfoObject.value.museumPrefix;
      textInfo.value.text.museumNumber =
        originalTextInfoObject.value.museumNumber;
      textInfo.value.text.publicationPrefix =
        originalTextInfoObject.value.primaryPublicationPrefix;
      textInfo.value.text.publicationNumber =
        originalTextInfoObject.value.primaryPublicationNumber;

      editText.value = !editText.value;
    };

    const getTextInfo = async () => {
      try {
        loading.value = true;
        if (localEpigraphyUnits) {
          textInfo.value = localEpigraphyUnits;
        } else if (textUuid) {
          textInfo.value = await server.getEpigraphicInfo(
            textUuid,
            forceAllowAdminView
          );
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'Error getting text epigraphy information. Please try again.',
          err as Error
        );
      } finally {
        loading.value = false;
      }
    };

    const getImageUrls = async () => {
      try {
        imagesLoading.value = true;
        if (localImageUrls) {
          imageUrls.value = localImageUrls;
        } else if (textUuid) {
          imageUrls.value = await server.getImageLinks(
            textUuid,
            textInfo.value.cdliNum
          );
        }
      } catch (err) {
        actions.showErrorSnackbar('Error getting image urls.', err as Error);
      } finally {
        imagesLoading.value = false;
      }
    };

    const editTextInfo = async () => {
      await updateTextInfo();
      editText.value = false;
    };

    const updateTextInfo = async () => {
      try {
        await server.updateTextInfo(
          textInfo.value.text.uuid,
          textInfo.value.text.excavationPrefix,
          textInfo.value.text.excavationNumber,
          textInfo.value.text.museumPrefix,
          textInfo.value.text.museumNumber,
          textInfo.value.text.publicationPrefix,
          textInfo.value.text.publicationNumber
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding new form. Please try again.',
          err as Error
        );
      }
    };

    onMounted(async () => {
      await getTextInfo();
      draft.value = textInfo.value.draft || null;
      zoteroDataList.value = textInfo.value.zoteroData;
      await getImageUrls();
    });

    const transliteration: ComputedRef<TranslitOption> = computed(() => ({
      color: textInfo.value.color,
      colorMeaning: textInfo.value.colorMeaning,
    }));

    provide(EpigraphyReloadKey, getTextInfo);

    const photosDialogOpen = ref(false);
    const photosToAdd = ref<TextPhoto[]>([]);
    const setPhotosToAdd = (photos: TextPhoto[]) => {
      photosToAdd.value = photos;
    };

    const uploadPhotos = async () => {
      try {
        const photosWithDetails = await addDetailsToTextPhotos(
          textInfo.value.text.excavationPrefix,
          textInfo.value.text.excavationNumber,
          textInfo.value.text.museumPrefix,
          textInfo.value.text.museumNumber,
          textInfo.value.text.publicationPrefix,
          textInfo.value.text.publicationNumber,
          photosToAdd.value
        );
        const resourceRows: ResourceRow[] = photosWithDetails.map(photo => ({
          uuid: v4(),
          sourceUuid: store.getters.user ? store.getters.user.uuid : null,
          type: 'img',
          container: 'oare-image-bucket',
          format: null,
          link: photo.name,
        }));

        const linkRows: LinkRow[] = resourceRows.map(resource => ({
          uuid: v4(),
          referenceUuid: textUuid || '',
          objUuid: resource.uuid,
        }));

        const itemPropertiesRows = photosWithDetails.flatMap((photo, idx) =>
          convertAppliedPropsToItemProps(
            photo.properties,
            resourceRows[idx].uuid
          )
        );

        await server.addPhotosToText(
          resourceRows,
          linkRows,
          itemPropertiesRows
        );
        await Promise.all(
          photosWithDetails.map(photo => server.uploadImage(photo))
        );
        photosToAdd.value.forEach(photo => {
          if (photo.url) {
            imageUrls.value.push({
              label: `${store.getters.user?.firstName} ${store.getters.user?.lastName}`,
              link: photo.url,
              side: photo.side || null,
              view: photo.view || null,
            });
          }
        });
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding photos to text. Please try again.',
          err as Error
        );
      }
    };

    const quarantineText = async () => {
      try {
        quarantineLoading.value = true;
        await server.quarantineText(textUuid!);
        quarantineDialog.value = false;
        router.push(`/collections/name/${textInfo.value.collection.uuid}`);
      } catch (err) {
        actions.showErrorSnackbar(
          'Error quarantining text. Please try again.',
          err as Error
        );
      } finally {
        quarantineLoading.value = false;
      }
    };

    const quarantineDialog = ref(false);
    const quarantineLoading = ref(false);

    const copyTransliteration = () => {
      const renderer = createTabletRenderer(
        textInfo.value.units,
        i18n.locale as LocaleCode,
        {
          lineNumbers: true,
        }
      );
      const transliterationString = renderer.getTransliterationString();
      navigator.clipboard.writeText(transliterationString);
      actions.showSnackbar('Copied transliteration to clipboard');
    };

    const hasCopyPermission = computed(() =>
      store.hasPermission('COPY_TEXT_TRANSLITERATION')
    );

    const seeMoreSwitch = () => {
      seeMoreZotero.value = !seeMoreZotero.value;
    };

    return {
      textInfo,
      isEditing,
      draftContent,
      breadcrumbItems,
      isAdmin,
      hasPicture,
      routeProps,
      routeActions,
      loading,
      canViewEpigraphyImages,
      imageUrls,
      getTextInfo,
      cancelEditTextInfo,
      toggleTextInfo,
      editTextInfo,
      updateTextInfo,
      originalTextInfoObject,
      hasEditPermission,
      transliteration,
      editText,
      photosDialogOpen,
      setPhotosToAdd,
      uploadPhotos,
      canAddPictures,
      quarantineText,
      quarantineDialog,
      quarantineLoading,
      zoteroDataList,
      allowViewCitations,
      copyTransliteration,
      hasCopyPermission,
      seeMoreZotero,
      seeMoreSwitch,
      imagesLoading,
      canComment,
      commentMode,
    };
  },
});
